import {DatePipe} from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {ApexAxisChartSeries, ApexOptions, ApexResponsive} from 'ng-apexcharts';

import {TerraNumberPipe} from 'src/app/shared/pipes/TerraNumber.pipe';
import {LpCashFlowChartData} from './LpCashFlowChartData.model';
import {LpCashFlowChartDataPoint} from './LpCashFlowChartDataPoint.model';
import {isEmpty} from 'lodash-es';

@Component({
  selector: 'terra-holding-cash-flow',
  templateUrl: './holding-cash-flow.component.html',
  styleUrls: ['./holding-cash-flow.component.scss', '../../../styles/holding/holding-page-shared.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HoldingPositionChartComponent implements OnInit, OnChanges {
  @Input() data: LpCashFlowChartData = null;
  @Input() showTitle = true;

  private isDisplayContributionsGraph = false;

  public chartOptions: Partial<ApexOptions>;

  constructor(
    private datePipe: DatePipe,
    private numberPipe: TerraNumberPipe
  ) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.data.currentValue && this.data) {
      this.initChart();
    }
  }

  ngOnInit() {
  }

  private initChart() {
    this.chartOptions = {
      series: this.getDataSeries(this.data),
      chart: {
        type: 'line',
        height: 350,
        stacked: true,
        stackOnlyBar: false,
        toolbar: {
          show: true,
        },
        events: {
          selection: (chart, e) => {
            console.log(new Date(e.xaxis.min));
          },
        },
      },
      stroke: {
        curve: 'straight',
        width: 3,
      },
      fill: {
        type: 'solid',
      },
      legend: {
        position: 'top',
        horizontalAlign: 'left',
      },
      grid: {
        padding: {
          top: 0,
          right: 0,
          bottom: 0,
          left: 10,
        },
      },
      xaxis: {
        type: 'category',

        labels: {
          formatter: (dateString: string, timestamp?: number, opts?: any) =>
            this.datePipe.transform(dateString, `MMM yy`),
          hideOverlappingLabels: true,
        },
        tickAmount: this.data.contributions?.length <= 20 ? 'dataPoints' : 20,
      },
      yaxis: {
        max: this.getMaxYValue(this.data),
        decimalsInFloat: 1,
        labels: {
          formatter: (val: number, opts?: any) => {
            return this.numberPipe.transform(Math.round(val / 1000), 1) + 'K';
          },
        },
      },
      tooltip: {
        y: {
          formatter: (y: number) => {
            if (typeof y !== 'undefined') {
              return `${this.numberPipe.transform(y)} ${this.data.currencyIso}`;
            }
            return y;
          },
        },
      },
      responsive: [
        {
          breakpoint: 650,
          options: {
            chart: {
              toolbar: {
                show: false,
              },
            },
            xaxis: {
              tickAmount: this.data.contributions?.length <= 8 ? 'dataPoints' : 8,
            }
          },
        } as ApexResponsive,
      ],
    };
  }


  private getDataSeries(data: LpCashFlowChartData): ApexAxisChartSeries {
    const dataSeries: ApexAxisChartSeries = [];

    if (this.isDisplayContributionsGraph) {
      dataSeries.push({
        name: 'Contributions',
        type: 'line',
        color: '#B02CCE',
        data: this.sumAmountsByTimestamp(data.contributions),
      });
    }

    dataSeries.push({
      name: `Return of ${this.data.hasDebt ? 'Principal' : 'Capital'}`,
      type: 'area',
      color: '#3D41EB',
      data: this.sumAmountsByTimestamp(data.returnOfCapital),
    });

    dataSeries.push({
      name: this.data.hasDebt ? 'Interest' : 'Profit',
      color: '#02B388',
      type: 'area',
      data: this.sumAmountsByTimestamp(data.revenue),
    });

    if (data?.revenue?.some(r => r.amount > 0) && data?.returnOfCapital.some(roc => roc.amount > 0)) {
      // sum incomes to one data point per timestamp
      const totalIncome = data.returnOfCapital.map((dataPoint, i) => {
        return {
          amount: dataPoint.amount + data.revenue[i].amount,
          timestamp: dataPoint.timestamp
        } as LpCashFlowChartDataPoint;
      });
      dataSeries.push({
        name: 'Total',
        type: 'line',
        color: '#002563',
        data: this.sumAmountsByTimestamp(totalIncome),
      });
    }

    return dataSeries;
  }

  private getMaxYValue(data: LpCashFlowChartData): number {
    if (isEmpty(data)) {
      return 0;
    }
    // Get the highest value from the contributions array:
    const contMax = this.isDisplayContributionsGraph ? Math.max(...data.contributions.map((val) => val.amount)) : 0;

    // The Return of capital and Revenue are stacked, so we should get the stacked highest value:
    const arr1 = [...data.returnOfCapital?.map((val) => val.amount)];
    const arr2 = [...data.revenue?.map((val) => val.amount)];

    // add the arr2 values to arr1:
    arr2.forEach((value, index) => {
      arr1[index] += value;
    });

    const stackedMaxValue = Math.max(...arr1.map((val) => val));

    return Math.max(contMax, stackedMaxValue);
  }

  private sumAmountsByTimestamp(data: any[] = []): { x: string; y: number }[] {
    const sumByTimestamp: { [timestamp: string]: number } = {};

    // Calculate sum of amounts for each timestamp
    data.forEach(({timestamp, amount}) => {
      sumByTimestamp[timestamp] = (sumByTimestamp[timestamp] || 0) + amount;
    });

    // Convert summed amounts into array of objects
    const summedArray = Object.entries(sumByTimestamp).map(([timestamp, amount]) => ({
      x: timestamp,
      y: amount
    }));

    return summedArray;
  }

}
